import React, { useRef, useEffect } from 'react';

const GAIN = '#00FF94';
const LOSS = '#EF4444';
const BRAND = '#6366F1';
const GOLD = '#FBBF24';

function generateCandles(count, startPrice, volatility) {
  const candles = [];
  let price = startPrice;
  for (let i = 0; i < count; i++) {
    const open = price;
    const change = (Math.random() - 0.48) * volatility;
    const close = open + change;
    const high = Math.max(open, close) + Math.random() * volatility * 0.5;
    const low = Math.min(open, close) - Math.random() * volatility * 0.5;
    candles.push({ open, close, high, low, bullish: close >= open, conf: 75 + Math.floor(Math.random() * 20) });
    price = close + (Math.random() - 0.5) * volatility * 0.2;
  }
  return candles;
}

function calcMA(candles, period) {
  const ma = [];
  for (let i = 0; i < candles.length; i++) {
    if (i < period - 1) { ma.push(null); continue; }
    let sum = 0;
    for (let j = i - period + 1; j <= i; j++) sum += candles[j].close;
    ma.push(sum / period);
  }
  return ma;
}

const ChartHeroBg = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let W, H;
    let animId;
    let frame = 0;

    const charts = [
      { candles: generateCandles(60, 100, 3), x: 0.02, y: 0.15, w: 0.5, h: 0.6, opacity: 0.25, candleWidth: 0.6, scrollSpeed: 0.3, offset: 0, showVolume: true, showMA: true, showGrid: true },
      { candles: generateCandles(45, 50, 2), x: 0.48, y: 0.1, w: 0.5, h: 0.5, opacity: 0.15, candleWidth: 0.55, scrollSpeed: 0.2, offset: 0, showVolume: true, showMA: true, showGrid: true },
      { candles: generateCandles(35, 200, 5), x: 0.05, y: 0.6, w: 0.35, h: 0.35, opacity: 0.12, candleWidth: 0.5, scrollSpeed: 0.4, offset: 0, showVolume: false, showMA: true, showGrid: false },
      { candles: generateCandles(30, 75, 2), x: 0.6, y: 0.55, w: 0.38, h: 0.35, opacity: 0.1, candleWidth: 0.5, scrollSpeed: 0.15, offset: 0, showVolume: false, showMA: false, showGrid: true },
    ];

    const particles = [];
    for (let i = 0; i < 25; i++) {
      particles.push({
        x: Math.random(), y: Math.random(),
        vx: (Math.random() - 0.5) * 0.0001,
        vy: (Math.random() - 0.5) * 0.00008,
        size: Math.random() * 1.5 + 0.5,
        opacity: Math.random() * 0.12 + 0.02,
        color: [GAIN, LOSS, BRAND, GOLD][Math.floor(Math.random() * 4)],
      });
    }

    const tickers = [];
    function spawnTicker() {
      const symbols = ['BTC/USD', 'ETH/USD', 'AAPL', 'TSLA', 'EUR/USD', 'XAU/USD', 'SPX', 'SOL/USD'];
      const sym = symbols[Math.floor(Math.random() * symbols.length)];
      const change = (Math.random() - 0.45) * 8;
      const price = (Math.random() * 5000 + 10).toFixed(2);
      tickers.push({
        sym, price, change, x: -0.15, y: 0.05 + Math.random() * 0.9,
        speed: 0.0003 + Math.random() * 0.0004,
        opacity: 0.15 + Math.random() * 0.1,
        size: 10 + Math.random() * 3,
      });
    }

    function resize() {
      const dpr = window.devicePixelRatio || 1;
      const parent = canvas.parentElement;
      if (!parent) return;
      W = parent.clientWidth;
      H = parent.clientHeight;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function drawChart(chart) {
      const cx = chart.x * W;
      const cy = chart.y * H;
      const cw = chart.w * W;
      const ch = chart.h * H;
      const candles = chart.candles;
      const candleSpacing = cw / candles.length;
      const candleW = candleSpacing * chart.candleWidth;

      let minP = Infinity, maxP = -Infinity;
      for (const c of candles) {
        if (c.low < minP) minP = c.low;
        if (c.high > maxP) maxP = c.high;
      }
      const priceRange = maxP - minP || 1;
      const chartH = ch * (chart.showVolume ? 0.75 : 1);

      function priceToY(p) {
        return cy + chartH - ((p - minP) / priceRange) * chartH * 0.9 - chartH * 0.05;
      }

      ctx.save();
      ctx.globalAlpha = chart.opacity;
      const scrollOff = chart.offset * candleSpacing;

      if (chart.showGrid) {
        ctx.strokeStyle = 'rgba(255,255,255,0.03)';
        ctx.lineWidth = 0.5;
        for (let i = 0; i < 5; i++) {
          const gy = cy + (chartH / 5) * i;
          ctx.beginPath(); ctx.moveTo(cx, gy); ctx.lineTo(cx + cw, gy); ctx.stroke();
        }
        for (let i = 0; i < candles.length; i += 5) {
          const gx = cx + i * candleSpacing - scrollOff % (candleSpacing * 5);
          ctx.beginPath(); ctx.moveTo(gx, cy); ctx.lineTo(gx, cy + ch); ctx.stroke();
        }
      }

      if (chart.showMA) {
        const ma20 = calcMA(candles, 7);
        ctx.strokeStyle = BRAND;
        ctx.globalAlpha = chart.opacity * 0.6;
        ctx.lineWidth = 1;
        ctx.beginPath();
        let started = false;
        for (let i = 0; i < candles.length; i++) {
          if (ma20[i] === null) continue;
          const px = cx + i * candleSpacing + candleW / 2 - scrollOff;
          const py = priceToY(ma20[i]);
          if (px < cx - 20 || px > cx + cw + 20) continue;
          if (!started) { ctx.moveTo(px, py); started = true; }
          else ctx.lineTo(px, py);
        }
        ctx.stroke();
        ctx.globalAlpha = chart.opacity;
      }

      for (let i = 0; i < candles.length; i++) {
        const c = candles[i];
        const px = cx + i * candleSpacing - scrollOff;
        if (px + candleW < cx - 10 || px > cx + cw + 10) continue;
        const color = c.bullish ? GAIN : LOSS;
        const bodyTop = priceToY(Math.max(c.open, c.close));
        const bodyBot = priceToY(Math.min(c.open, c.close));
        const bodyH = Math.max(1, bodyBot - bodyTop);

        ctx.strokeStyle = color; ctx.lineWidth = 0.8;
        ctx.beginPath();
        ctx.moveTo(px + candleW / 2, priceToY(c.high));
        ctx.lineTo(px + candleW / 2, priceToY(c.low));
        ctx.stroke();

        if (c.bullish) {
          ctx.strokeStyle = color; ctx.lineWidth = 0.8;
          ctx.strokeRect(px, bodyTop, candleW, bodyH);
          ctx.fillStyle = 'rgba(0, 255, 148, 0.15)';
          ctx.fillRect(px, bodyTop, candleW, bodyH);
        } else {
          ctx.fillStyle = color;
          ctx.fillRect(px, bodyTop, candleW, bodyH);
        }
      }

      for (let i = 2; i < candles.length; i++) {
        const c = candles[i];
        const prev = candles[i - 1];
        const prev2 = candles[i - 2];
        const px = cx + i * candleSpacing + candleW / 2 - scrollOff;
        if (px < cx - 10 || px > cx + cw + 10) continue;

        const isBuy = !prev2.bullish && !prev.bullish && c.bullish && c.close > prev.open;
        const isSell = prev2.bullish && prev.bullish && !c.bullish && c.close < prev.open;

        if (isBuy) {
          const sy = priceToY(c.low) + 14;
          const targetY = priceToY(c.close + (c.close - c.low) * 1.5);
          ctx.setLineDash([3, 3]); ctx.strokeStyle = GAIN;
          ctx.globalAlpha = chart.opacity * 0.3; ctx.lineWidth = 0.6;
          ctx.beginPath(); ctx.moveTo(px, priceToY(c.close)); ctx.lineTo(px + candleSpacing * 8, targetY); ctx.stroke();
          ctx.setLineDash([]);
          ctx.font = '7px monospace'; ctx.fillStyle = GAIN; ctx.globalAlpha = chart.opacity * 0.4;
          ctx.textAlign = 'left'; ctx.fillText('TP', px + candleSpacing * 8 + 3, targetY + 3);
          ctx.globalAlpha = chart.opacity;

          const glow = ctx.createRadialGradient(px, sy, 0, px, sy, 16);
          glow.addColorStop(0, 'rgba(0, 255, 148, 0.25)'); glow.addColorStop(1, 'transparent');
          ctx.fillStyle = glow; ctx.beginPath(); ctx.arc(px, sy, 16, 0, Math.PI * 2); ctx.fill();

          ctx.fillStyle = GAIN; ctx.beginPath();
          ctx.moveTo(px, sy - 6); ctx.lineTo(px - 5, sy + 3); ctx.lineTo(px + 5, sy + 3); ctx.closePath(); ctx.fill();
          ctx.font = 'bold 8px monospace'; ctx.fillStyle = GAIN; ctx.textAlign = 'center'; ctx.fillText('BUY', px, sy + 14);
          ctx.font = '6px monospace'; ctx.globalAlpha = chart.opacity * 0.5; ctx.fillText('AI ' + c.conf + '%', px, sy + 22);
          ctx.globalAlpha = chart.opacity;
          const pulseR = 8 + Math.sin(frame * 0.06) * 4;
          ctx.strokeStyle = GAIN; ctx.globalAlpha = chart.opacity * (0.4 - Math.sin(frame * 0.06) * 0.2); ctx.lineWidth = 0.8;
          ctx.beginPath(); ctx.arc(px, sy - 2, pulseR, 0, Math.PI * 2); ctx.stroke();
          ctx.globalAlpha = chart.opacity;
        }

        if (isSell) {
          const sy = priceToY(c.high) - 14;
          const targetY = priceToY(c.close - (c.high - c.close) * 1.5);
          ctx.setLineDash([3, 3]); ctx.strokeStyle = LOSS;
          ctx.globalAlpha = chart.opacity * 0.3; ctx.lineWidth = 0.6;
          ctx.beginPath(); ctx.moveTo(px, priceToY(c.close)); ctx.lineTo(px + candleSpacing * 8, targetY); ctx.stroke();
          ctx.setLineDash([]);
          ctx.font = '7px monospace'; ctx.fillStyle = LOSS; ctx.globalAlpha = chart.opacity * 0.4;
          ctx.textAlign = 'left'; ctx.fillText('TP', px + candleSpacing * 8 + 3, targetY + 3);
          ctx.globalAlpha = chart.opacity;

          const glow = ctx.createRadialGradient(px, sy, 0, px, sy, 16);
          glow.addColorStop(0, 'rgba(239, 68, 68, 0.25)'); glow.addColorStop(1, 'transparent');
          ctx.fillStyle = glow; ctx.beginPath(); ctx.arc(px, sy, 16, 0, Math.PI * 2); ctx.fill();

          ctx.fillStyle = LOSS; ctx.beginPath();
          ctx.moveTo(px, sy + 6); ctx.lineTo(px - 5, sy - 3); ctx.lineTo(px + 5, sy - 3); ctx.closePath(); ctx.fill();
          ctx.font = 'bold 8px monospace'; ctx.fillStyle = LOSS; ctx.textAlign = 'center'; ctx.fillText('SELL', px, sy - 10);
          ctx.font = '6px monospace'; ctx.globalAlpha = chart.opacity * 0.5; ctx.fillText('AI ' + c.conf + '%', px, sy - 18);
          ctx.globalAlpha = chart.opacity;
          const pulseR = 8 + Math.sin(frame * 0.06 + Math.PI) * 4;
          ctx.strokeStyle = LOSS; ctx.globalAlpha = chart.opacity * (0.4 - Math.sin(frame * 0.06 + Math.PI) * 0.2); ctx.lineWidth = 0.8;
          ctx.beginPath(); ctx.arc(px, sy + 2, pulseR, 0, Math.PI * 2); ctx.stroke();
          ctx.globalAlpha = chart.opacity;
        }
      }

      if (chart.showVolume) {
        const volH = ch * 0.2;
        const volY = cy + chartH + ch * 0.02;
        for (let i = 0; i < candles.length; i++) {
          const c = candles[i];
          const px = cx + i * candleSpacing - scrollOff;
          if (px + candleW < cx - 10 || px > cx + cw + 10) continue;
          const vol = Math.abs(c.close - c.open) / priceRange;
          const barH = vol * volH * 8 + volH * 0.1;
          const color = c.bullish ? GAIN : LOSS;
          ctx.fillStyle = color; ctx.globalAlpha = chart.opacity * 0.3;
          ctx.fillRect(px, volY + volH - barH, candleW, barH);
          ctx.globalAlpha = chart.opacity;
        }
      }

      ctx.strokeStyle = 'rgba(255,255,255,0.03)'; ctx.lineWidth = 0.5;
      ctx.strokeRect(cx, cy, cw, ch);
      ctx.restore();
    }

    function drawLineChart(startX, startY, width, height, opacity, speed) {
      ctx.save();
      ctx.globalAlpha = opacity;
      const points = 80;
      const linePoints = [];
      for (let i = 0; i < points; i++) {
        const t = (i / points + frame * speed * 0.001) % 1;
        const x = startX * W + (i / points) * width * W;
        const noise = Math.sin(t * Math.PI * 6 + i * 0.3) * 0.3 +
                      Math.sin(t * Math.PI * 3 + i * 0.7) * 0.2 +
                      Math.cos(t * Math.PI * 8 + i * 0.1) * 0.15;
        const y = startY * H + height * H * 0.5 + noise * height * H * 0.4;
        linePoints.push({ x, y });
      }

      const grad = ctx.createLinearGradient(0, startY * H, 0, (startY + height) * H);
      grad.addColorStop(0, 'rgba(99, 102, 241, 0.08)');
      grad.addColorStop(1, 'transparent');
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.moveTo(linePoints[0].x, (startY + height) * H);
      for (const p of linePoints) ctx.lineTo(p.x, p.y);
      ctx.lineTo(linePoints[linePoints.length - 1].x, (startY + height) * H);
      ctx.closePath();
      ctx.fill();

      ctx.strokeStyle = BRAND; ctx.lineWidth = 1.2;
      ctx.beginPath();
      for (let i = 0; i < linePoints.length; i++) {
        if (i === 0) ctx.moveTo(linePoints[i].x, linePoints[i].y);
        else ctx.lineTo(linePoints[i].x, linePoints[i].y);
      }
      ctx.stroke();

      const last = linePoints[linePoints.length - 1];
      const glow = ctx.createRadialGradient(last.x, last.y, 0, last.x, last.y, 8);
      glow.addColorStop(0, BRAND + '88');
      glow.addColorStop(1, 'transparent');
      ctx.fillStyle = glow; ctx.beginPath(); ctx.arc(last.x, last.y, 8, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#fff'; ctx.beginPath(); ctx.arc(last.x, last.y, 2, 0, Math.PI * 2); ctx.fill();
      ctx.restore();
    }

    function draw() {
      ctx.clearRect(0, 0, W, H);
      frame++;

      for (const chart of charts) {
        chart.offset += chart.scrollSpeed * 0.016;
        if (chart.offset > 1) {
          chart.offset -= 1;
          const last = chart.candles[chart.candles.length - 1];
          chart.candles.shift();
          const vol = Math.abs(last.close - last.open) * 0.5 + 1;
          const open = last.close + (Math.random() - 0.5) * vol * 0.3;
          const change = (Math.random() - 0.48) * vol;
          const close = open + change;
          chart.candles.push({
            open, close,
            high: Math.max(open, close) + Math.random() * vol * 0.5,
            low: Math.min(open, close) - Math.random() * vol * 0.5,
            bullish: close >= open,
            conf: 75 + Math.floor(Math.random() * 20),
          });
        }
      }

      for (const chart of charts) drawChart(chart);
      drawLineChart(0.1, 0.05, 0.4, 0.25, 0.08, 0.5);
      drawLineChart(0.5, 0.7, 0.45, 0.2, 0.06, 0.3);

      if (frame % 120 === 0) spawnTicker();
      if (frame % 180 === 0) spawnTicker();

      for (let i = tickers.length - 1; i >= 0; i--) {
        const t = tickers[i];
        t.x += t.speed;
        if (t.x > 1.2) { tickers.splice(i, 1); continue; }
        ctx.globalAlpha = t.opacity;
        const tx = t.x * W;
        const ty = t.y * H;

        ctx.fillStyle = 'rgba(255,255,255,0.02)';
        const pillW = t.size * 12;
        const pillH = t.size * 2;
        ctx.beginPath();
        ctx.roundRect(tx, ty - pillH / 2, pillW, pillH, pillH / 2);
        ctx.fill();

        ctx.font = `${t.size}px monospace`;
        ctx.fillStyle = 'rgba(255,255,255,0.4)'; ctx.textAlign = 'left';
        ctx.fillText(t.sym, tx + 8, ty + t.size * 0.35);
        ctx.fillStyle = 'rgba(255,255,255,0.25)';
        ctx.fillText(t.price, tx + t.size * 5.5, ty + t.size * 0.35);
        const changeColor = t.change >= 0 ? GAIN : LOSS;
        ctx.fillStyle = changeColor;
        const sign = t.change >= 0 ? '+' : '';
        ctx.fillText(`${sign}${t.change.toFixed(2)}%`, tx + t.size * 9, ty + t.size * 0.35);
        ctx.globalAlpha = 1;
      }

      for (const p of particles) {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = 1; if (p.x > 1) p.x = 0;
        if (p.y < 0) p.y = 1; if (p.y > 1) p.y = 0;
        ctx.fillStyle = p.color; ctx.globalAlpha = p.opacity;
        ctx.beginPath(); ctx.arc(p.x * W, p.y * H, p.size, 0, Math.PI * 2); ctx.fill();
        ctx.globalAlpha = 1;
      }

      const scanY = (frame * 0.5) % H;
      const scanGrad = ctx.createLinearGradient(0, scanY - 2, 0, scanY + 2);
      scanGrad.addColorStop(0, 'transparent');
      scanGrad.addColorStop(0.5, 'rgba(99, 102, 241, 0.03)');
      scanGrad.addColorStop(1, 'transparent');
      ctx.fillStyle = scanGrad;
      ctx.fillRect(0, scanY - 2, W, 4);

      animId = requestAnimationFrame(draw);
    }

    resize();
    spawnTicker(); spawnTicker(); spawnTicker();
    draw();

    window.addEventListener('resize', resize);
    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
      }}
    />
  );
};

export default ChartHeroBg;
